/**
 * Totales de las ventas de un petshop (sin las anuladas): el total, cuánto por
 * método de pago y cuánto de cada producto. Lo usan las pantallas de Petshops
 * y los archivos (Excel y PDF).
 */
import type { PetshopVenta } from '@/types/domain';

export const METODO_LABEL: Record<string, string> = {
  efectivo: 'Efectivo',
  qr: 'QR',
  transferencia: 'Transferencia',
  tarjeta: 'Tarjeta',
  otro: 'Otro',
};

/** El nombre del producto sin la cantidad («Collar ×5» → «Collar»). */
export function nombreProducto(v: PetshopVenta): string {
  return (v.descripcion ?? '').replace(/\s*×\s*\d+(?:[.,]\d+)?\s*$/, '').trim() || 'Sin nombre';
}

export interface Totales {
  total: number;
  ventas: number;
  porMetodo: { metodo: string; label: string; total: number }[];
  porProducto: { nombre: string; unidades: number; total: number }[];
}

/** Los totales sin las anuladas. */
export function totalesDe(ventas: PetshopVenta[]): Totales {
  const validas = ventas.filter((v) => !v.anulada);
  const metodos = new Map<string, number>();
  const productos = new Map<string, { nombre: string; unidades: number; total: number }>();
  for (const v of validas) {
    metodos.set(v.metodoPago, (metodos.get(v.metodoPago) ?? 0) + (v.monto ?? 0));
    const nombre = nombreProducto(v);
    const clave = v.productoId ?? nombre.toLowerCase();
    const p = productos.get(clave) ?? { nombre, unidades: 0, total: 0 };
    p.unidades += Number(v.cantidad ?? 1) || 1;
    p.total += v.monto ?? 0;
    productos.set(clave, p);
  }
  const orden = Object.keys(METODO_LABEL);
  return {
    total: validas.reduce((s, v) => s + (v.monto ?? 0), 0),
    ventas: validas.length,
    porMetodo: [...metodos.entries()]
      .map(([metodo, total]) => ({ metodo, label: METODO_LABEL[metodo] ?? metodo, total }))
      .sort((a, b) => orden.indexOf(a.metodo) - orden.indexOf(b.metodo)),
    porProducto: [...productos.values()].sort((a, b) => b.total - a.total || a.nombre.localeCompare(b.nombre)),
  };
}
